// src/pages/PrivacyPolicyPage.jsx
// Linked from the footer, the Contact form, the sign-up checkbox and the
// cookie banner area. Same layout as ShippingPolicyPage.jsx / CookiePolicyPage.jsx.
//
// What this page says must match what the site really does. When a form field,
// data processor (Firebase, Zoho, Shiprocket, Google) or tag is added or
// removed, update the lists below and LAST_UPDATED. Written to the spirit of
// India's Digital Personal Data Protection Act, 2023; have counsel review
// before relying on it as a legal document.
import React from "react";
import "./ContactPage.css";
import Footer from "../Footer";
import { openCookieSettings } from "../utils/cookieConsent";

const LAST_UPDATED = "1 October 2026";
const EMAIL = "info@bibaelectronics.com";

const COLLECTED = [
  ["Contact / enquiry form", "Name, company, phone, email, city, your requirement", "To reply to your enquiry and prepare a quote"],
  ["Catalogue download", "Name, company, email, phone", "To send you the catalogue and follow up on your interest"],
  ["Newsletter sign-up", "Email address, the page you signed up on", "To send product and company updates, only after you subscribe"],
  ["Account and sign-in", "Name, email, phone number, company details, sign-in method (password, email link, Google or phone OTP)", "To create and secure your account and show your orders"],
  ["Orders and delivery", "Items, billing and shipping address, phone, order and payment status", "To process, ship, invoice and support your order"],
  ["Careers", "Name, contact details, resume and the details you submit", "To assess your job application"],
  ["Cookies and analytics", "Pages visited, device and browser type, approximate location, ad-click information", "To understand site use and measure advertising, only if you accept (see below)"],
];

const SHARED = [
  ["Google Firebase (Google LLC)", "Hosts our website database, sign-in and file storage"],
  ["Zoho (Zoho Corporation)", "Our accounting, invoicing and payment collection (Zoho Pay)"],
  ["Shiprocket", "Courier booking and delivery tracking for your order; receives your name, address and phone"],
  ["Google Analytics, Google Ads and Tag Manager", "Visitor statistics and ad measurement, only if you accept cookies"],
  ["Google reCAPTCHA", "Protects sign-in and forms from bots"],
];

const h2 = { fontSize: "1.3rem", margin: "28px 0 8px" };
const th = { padding: "8px 10px" };
const td = { padding: "8px 10px", verticalAlign: "top" };

function Table({ head, rows }) {
  return (
    <div style={{ overflowX: "auto", margin: "16px 0" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.92rem" }}>
        <thead>
          <tr style={{ textAlign: "left", borderBottom: "2px solid #e5e7eb" }}>
            {head.map((h) => (
              <th key={h} style={th}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row[0]} style={{ borderBottom: "1px solid #e5e7eb" }}>
              {row.map((cell, i) => (
                <td key={i} style={td}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const PrivacyPolicyPage = () => {
  return (
    <div className="cp-root">
      <div className="cp-breadcrumb-bar">
        <div className="cp-breadcrumbs">
          <a href="/">Home</a>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">Privacy Policy</span>
        </div>
      </div>

      <section className="cp-rfq-section">
        <div className="cp-wrap" style={{ maxWidth: 860 }}>
          <span className="cp-badge">PRIVACY</span>
          <h1 style={{ margin: "16px 0 8px", fontSize: "2rem" }}>Privacy Policy</h1>
          <p style={{ margin: "0 0 24px", color: "#6b7280", fontSize: "0.9rem" }}>Last updated {LAST_UPDATED}</p>

          <div style={{ fontSize: "1rem", lineHeight: 1.8, color: "#374151" }}>
            <p>
              BIBA Electronics Pvt. Ltd. ("BIBA", "we") runs bibaelectronics.com. This page explains what personal
              data we collect on this website, why, who we share it with, and the choices you have. We collect only
              what we need and we do not sell your personal data.
            </p>

            <h2 style={h2}>Who we are</h2>
            <p>
              BIBA Electronics Pvt. Ltd., 357E, Pace City II, Sector 37, Gurugram, Haryana 122004, India. For anything
              about your data, write to <a href={`mailto:${EMAIL}`}>{EMAIL}</a>.
            </p>

            <h2 style={h2}>What we collect and why</h2>
            <Table head={["Where", "What", "Why"]} rows={COLLECTED} />
            <p>
              We process this data because you gave it to us (consent), or to carry out an order or enquiry you
              made, or to meet legal duties such as tax invoicing.
            </p>

            <h2 style={h2}>Who we share it with</h2>
            <p>
              We use these service providers to run the website. They handle data only for the purposes below and
              under their own privacy terms.
            </p>
            <Table head={["Provider", "What it does for us"]} rows={SHARED} />
            <p>
              Some of these providers process data outside India. We may also disclose data where the law or a
              government authority requires it. We do not sell your data or share it for third-party marketing.
            </p>

            <h2 style={h2}>Cookies</h2>
            <p>
              Analytics and advertising cookies are off until you accept them. Details are in our{" "}
              <a href="/about/cookie-policy">Cookie Policy</a>.{" "}
              <button
                type="button"
                onClick={openCookieSettings}
                style={{ background: "none", border: 0, padding: 0, color: "#15803d", textDecoration: "underline", cursor: "pointer", font: "inherit" }}
              >
                Change your cookie choice
              </button>
            </p>

            <h2 style={h2}>How long we keep it</h2>
            <p>
              Enquiries and catalogue requests: up to 3 years after our last contact. Order, invoice and payment
              records: as long as tax and accounting law requires (generally 8 years). Newsletter: until you
              unsubscribe. Job applications: up to 1 year. Account data: while your account is open and for a
              reasonable period after you close it.
            </p>

            <h2 style={h2}>Your rights</h2>
            <p>
              You can ask us to show you the personal data we hold about you, correct it, delete it, or stop using it
              for something you earlier agreed to, such as the newsletter or cookies. You can withdraw consent at any
              time. Write to <a href={`mailto:${EMAIL}`}>{EMAIL}</a> from the email address we have on record and we
              will reply within 30 days. If you are not satisfied, you may complain to the Data Protection Board of
              India.
            </p>

            <h2 style={h2}>Security</h2>
            <p>
              Data is held in Google Firebase and Zoho with access limited to our staff who need it, and the site is
              served over HTTPS. No online system is perfectly secure, so we cannot promise absolute security, but we
              will tell affected people and the authorities if a breach puts your data at risk.
            </p>

            <h2 style={h2}>Children</h2>
            <p>This website is for businesses and adults. We do not knowingly collect data from anyone under 18.</p>

            <h2 style={h2}>Changes to this policy</h2>
            <p>
              If we change what we collect or how we use it, we will update this page and its date. For material
              changes we will ask for your consent again where the law requires.
            </p>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
